/**
 * Correct a card's translations and example, mid-practice.
 *
 * The deck is 2,425 entries authored in one pass and still marked
 * `auto-pending-review`, so the moment you are most likely to notice a wrong
 * gloss is the moment it is shown to you. Making the fix reachable from there —
 * rather than from `seed/*.json` and a re-seed — is the whole point.
 *
 * Saving sets `cards.user_edited`, which stops the seeder's upsert from
 * overwriting the correction on the next deploy. Without that flag this dialog
 * would appear to work and quietly lose every edit.
 */
import { useState } from 'react';
import { Pencil } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { Card } from '@/core/types';
import { api, ApiError } from '@/lib/api';
import { LANG_NAMES } from '@/lib/lang-flags';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  card: Card<any>;
  primary_lang: string;
  secondary_lang: string;
  /** Lets the parent show the corrected values without refetching the session. */
  onSaved: (patch: {
    translations: Record<string, string[]>;
    example: string | null;
    example_translations: Record<string, string>;
  }) => void;
};

/** Comma-separated in the field, a list in the payload. */
const toList = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

export function EditCardDialog({ card, primary_lang, secondary_lang, onSaved }: Props) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [primary, setPrimary] = useState((card.translations[primary_lang] ?? []).join(', '));
  const [secondary, setSecondary] = useState((card.translations[secondary_lang] ?? []).join(', '));
  const [example, setExample] = useState(card.example ?? '');
  const [exampleGloss, setExampleGloss] = useState(card.example_translations[secondary_lang] ?? '');

  const save = async () => {
    if (toList(primary).length === 0 && toList(secondary).length === 0) {
      toast.error('A card needs at least one translation');
      return;
    }
    const patch = {
      translations: {
        ...card.translations,
        [primary_lang]: toList(primary),
        [secondary_lang]: toList(secondary),
      },
      example: example.trim() || null,
      example_translations: exampleGloss.trim()
        ? { ...card.example_translations, [secondary_lang]: exampleGloss.trim() }
        : {},
    };
    setSaving(true);
    try {
      await api.editCard(card.id, patch);
      onSaved(patch);
      setOpen(false);
      toast.success('Card updated', {
        description: 'Your version is kept — re-seeding will not overwrite it.',
      });
    } catch (err) {
      toast.error('Could not save', {
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Correct this card"
        title="Correct this card"
        className="text-muted-foreground hover:bg-muted focus-visible:ring-ring inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors focus-visible:ring-2 focus-visible:outline-none"
      >
        <Pencil className="size-4" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct “{card.de}”</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <Field
              id="edit-primary"
              label={`${LANG_NAMES[primary_lang] ?? primary_lang} — separate with commas`}
              value={primary}
              onChange={setPrimary}
            />
            <Field
              id="edit-secondary"
              label={`${LANG_NAMES[secondary_lang] ?? secondary_lang} — separate with commas`}
              value={secondary}
              onChange={setSecondary}
            />
            <Field
              id="edit-example"
              label="Example sentence"
              value={example}
              onChange={setExample}
            />
            <Field
              id="edit-example-gloss"
              label="Example translation"
              value={exampleGloss}
              onChange={setExampleGloss}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save correction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
